import EncoreLogo from '@/assets/cortile/clients/encore.svg';
import JfmLogo from '@/assets/cortile/clients/jfm.svg';
import PbmLogo from '@/assets/cortile/clients/pbm.svg';
import PinnacleLogo from '@/assets/cortile/clients/pinnacle-white.svg';
import RmgLogo from '@/assets/cortile/clients/rmg.svg';
import CortileSymbolCream from '@/assets/cortile/symbol-cream.svg';
import CortileWordmarkCream from '@/assets/cortile/wordmark-cream.svg';
import {Box, keyframes, Typography, TypographyProps} from '@mui/material';
import React from 'react';
import {CORTILE, FONT} from './tokens';

type ClientLogo = {
  name: string;
  src: string;
  url: string;
  height: number;
  invert: boolean;
};

const CLIENT_LOGOS: ClientLogo[] = [
  {
    name: 'Pinnacle Group',
    src: PinnacleLogo,
    url: 'https://www.pinnaclegroup.co.uk/',
    height: 40,
    invert: false,
  },
  {
    name: 'RMG',
    src: RmgLogo,
    url: 'https://www.rmguk.com/',
    height: 26,
    invert: true,
  },
  {
    name: 'JFM',
    src: JfmLogo,
    url: 'https://www.jfm-management.co.uk/',
    height: 30,
    invert: true,
  },
  {
    name: 'PBM',
    src: PbmLogo,
    url: 'https://www.pbm-property.com/',
    height: 30,
    invert: true,
  },
  {
    name: 'Encore Estates',
    src: EncoreLogo,
    url: 'https://www.encoreestates.co.uk/',
    height: 15,
    invert: true,
  },
];

const REFERRAL =
  'utm_source=cortile&utm_medium=referral&utm_campaign=login_page';

const glint = keyframes`
  0%, 62% { background-position: 130% 0; }
  100% { background-position: -30% 0; }
`;

type PanelTextProps = TypographyProps & {component?: React.ElementType};

const PanelText = ({sx, ...props}: PanelTextProps) => (
  <Typography
    {...props}
    sx={[
      {
        position: 'relative',
        zIndex: 2,
        m: 0,
        fontFamily: FONT,
        color: CORTILE.cream,
      },
      ...(Array.isArray(sx) ? sx : [sx]),
    ]}
  />
);

type Props = {
  layerRef: React.Ref<HTMLDivElement>;
  reducedMotion: boolean;
};

export const CortileBrandPanel = ({layerRef, reducedMotion}: Props) => (
  <Box
    sx={{
      bgcolor: CORTILE.terracotta,
      position: 'relative',
      overflow: 'hidden',
      px: 7,
      py: 'clamp(32px, 6vh, 56px)',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      gap: 4,
      minWidth: 0,
    }}
  >
    <Box
      component="img"
      src={CortileSymbolCream}
      alt=""
      sx={{
        position: 'absolute',
        width: 860,
        height: 860,
        left: -400,
        bottom: -400,
        opacity: 0.13,
        transform: 'rotate(-22deg)',
        pointerEvents: 'none',
      }}
    />
    <Box
      ref={layerRef}
      sx={{position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 1}}
    />
    <Box
      component="img"
      src={CortileWordmarkCream}
      alt="Cortile"
      sx={{
        height: 30,
        width: 'auto',
        alignSelf: 'flex-start',
        position: 'relative',
        zIndex: 2,
        pointerEvents: 'none',
      }}
    />
    <Box
      sx={{
        position: 'relative',
        zIndex: 2,
        display: 'flex',
        flexDirection: 'column',
        gap: 'clamp(16px, 2.8vh, 28px)',
        maxWidth: 440,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 'clamp(10px, 2vh, 20px)',
        }}
      >
        <PanelText
          component="h1"
          sx={{
            fontSize: 'clamp(34px, 5.6vh, 52px)',
            fontWeight: 600,
            lineHeight: 1.08,
            letterSpacing: '-0.03em',
            textWrap: 'balance',
          }}
        >
          The centre of every building.
        </PanelText>
        <PanelText
          sx={{
            fontSize: 'clamp(16px, 2vh, 19px)',
            lineHeight: 1.5,
            textWrap: 'pretty',
          }}
        >
          The operating platform for residential property management.
        </PanelText>
      </Box>
      <Box sx={{height: '1px', bgcolor: 'rgba(255, 253, 248, 0.28)'}} />
      <Box
        sx={{
          display: 'flex',
          alignItems: 'baseline',
          gap: 1.5,
          flexWrap: 'wrap',
        }}
      >
        <PanelText
          component="span"
          sx={{
            fontSize: 'clamp(30px, 4vh, 38px)',
            fontWeight: 700,
            letterSpacing: '-0.03em',
          }}
        >
          400,000+
        </PanelText>
        <PanelText component="span" sx={{fontSize: 16}}>
          homes managed by Cortile customers
        </PanelText>
      </Box>
      <Box sx={{display: 'flex', flexDirection: 'column', gap: 2}}>
        <PanelText
          component="span"
          sx={{
            fontSize: 12,
            fontWeight: 700,
            letterSpacing: '0.14em',
            textTransform: 'uppercase',
          }}
        >
          Trusted by leading managing agents
        </PanelText>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 3.5,
            flexWrap: 'wrap',
          }}
        >
          {CLIENT_LOGOS.map(logo => (
            <Box
              key={logo.name}
              component="a"
              href={`${logo.url}?${REFERRAL}`}
              target="_blank"
              rel="noopener"
              aria-label={`${logo.name} (opens in a new tab)`}
              title={logo.name}
              sx={{
                display: 'block',
                opacity: 0.82,
                transition: 'opacity 160ms ease, transform 160ms ease',
                '&:hover, &:focus-visible': {
                  opacity: 1,
                  transform: reducedMotion ? 'none' : 'translateY(-2px)',
                },
                '&:focus-visible': {
                  outline: `2px solid ${CORTILE.cream}`,
                  outlineOffset: 4,
                  borderRadius: '2px',
                },
              }}
            >
              <Box
                component="img"
                src={logo.src}
                alt={logo.name}
                sx={{
                  display: 'block',
                  height: logo.height,
                  width: 'auto',
                  filter: logo.invert ? 'brightness(0) invert(1)' : 'none',
                }}
              />
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
    <PanelText
      sx={{
        fontSize: 18,
        fontWeight: 600,
        lineHeight: 1.45,
        pointerEvents: 'none',
      }}
    >
      <Box
        component="span"
        sx={{
          backgroundImage:
            'linear-gradient(100deg, #FFFDF8 0%, #FFFDF8 40%, #FFD9C4 47%, #FFFFFF 50%, #FFD9C4 53%, #FFFDF8 60%, #FFFDF8 100%)',
          backgroundSize: '250% 100%',
          backgroundPosition: '130% 0',
          WebkitBackgroundClip: 'text',
          backgroundClip: 'text',
          color: 'transparent',
          animation: reducedMotion
            ? 'none'
            : `${glint} 4.5s cubic-bezier(.4,0,.2,1) 1.2s infinite`,
        }}
      >
        Formerly Ark Residential.
      </Box>
    </PanelText>
  </Box>
);
